// ─────────────────────────────────────────────────────────────────────────────
// Live Fairness Check
//
// Surfaces the Field Time / fairness rules DURING the game (between innings),
// not just inside the auto-lineup generator. Pure, dependency-free, read-only:
// it never writes Firestore and never touches React. It reads the same
// `inningLogs` shape the lineup engine and StatsView already use, and the same
// position/bench conventions (BENCH/EH = not in field; infield = P,C,1B,2B,SS,3B)
// so the live check and the generator can never disagree.
//
// Voice constraint (hard): observations only, never directives. We report the
// STATE ("Tommy has sat the last 2 innings"), never an order ("move Tommy").
// ─────────────────────────────────────────────────────────────────────────────

export interface FairnessFinding {
  rule: string;       // which toggle produced this (for keys / future filtering)
  players: string[];  // player ids involved
  message: string;    // observation-only copy, already name-rendered
  priority: number;   // lower = more important; used to rank + cap the live box
}

// Priority ranking — playing-time problems a coach loses sleep over come first;
// position repeats are cosmetic and sink to the bottom. The live box shows the
// top few by this order.
const PRIORITY = {
  noConsecutiveBench: 1,   // a kid stuck on the bench — the headline fairness sin
  minFieldInnings: 2,      // someone won't reach their innings floor
  mustPlayInfield: 3,      // hasn't touched the infield
  equalBenchInnings: 4,    // bench is lopsided
  noConsecutivePosition: 5,// holding a spot — least urgent, often just stable D
} as const;

interface LineupEntry {
  id: string;
  position: string;
  name?: string;
}

type InningLogs = Record<number, { id: string; position: string }[]>;

const INFIELD = new Set(['P', 'C', '1B', '2B', 'SS', '3B']);
const BENCHED = (pos: string | undefined): boolean => pos === 'BENCH' || pos === 'EH';

// True iff at least one Field Time rule is enabled. The whole feature is gated
// on this — if it's false the coach has opted out and we show nothing.
export const anyFairnessRuleOn = (s: any): boolean =>
  !!(s?.fieldTimeEqualizerEnabled || s?.minFieldInningsEnabled || s?.equalBenchInnings
    || s?.noConsecutiveBench || s?.noConsecutivePosition || s?.mustPlayInfieldEnabled);

// Inning numbers in chronological order.
const sortedInningKeys = (logs: InningLogs): number[] =>
  Object.keys(logs).map(Number).sort((a, b) => a - b);

// Fold a list of player ids into a short readable subject, capping at 2 names so
// a line never becomes a roster dump: "Alice", "Alice and Bob", "Alice, Bob and
// 3 others". Keeps the live box scannable no matter how many players trip a rule.
const foldNames = (ids: string[], names: Record<string, string>): string => {
  const list = ids.map(id => names[id] || 'A player');
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  const extra = list.length - 2;
  return `${list[0]}, ${list[1]} and ${extra} ${extra === 1 ? 'other' : 'others'}`;
};

const haveHas = (n: number) => (n === 1 ? 'has' : 'have');
const areIs = (n: number) => (n === 1 ? 'is' : 'are');

// ─────────────────────────────────────────────────────────────────────────────
// analyzeFairness
//
// `lineup` is the alignment being evaluated. For Checkpoint A (the between-innings
// review) this is the inning that just ended — already saved into `inningLogs` —
// so the lineup arg simply mirrors the latest log. The signature also supports a
// future Checkpoint B that passes the in-progress NEXT alignment to re-check.
//
// `totalInningsPlanned` lets the min-innings rule project "behind pace" before
// it's mathematically too late. If omitted we skip the pace projection.
// ─────────────────────────────────────────────────────────────────────────────
export function analyzeFairness(
  lineup: LineupEntry[],
  inningLogs: InningLogs,
  settings: any,
  rosterNames: Record<string, string>,
  totalInningsPlanned?: number,
  // Players pulled from the game (injury, left early). They keep the innings
  // they already played, but no rule should expect more from them, so they are
  // excluded from every finding below.
  removedPlayerIds?: string[],
  // When true, `lineup` is the UPCOMING inning (not one already logged), so a
  // problem the coach has already solved there must not be reported. Checkpoint B
  // passes this; Checkpoint A reviews the inning just played and leaves it off.
  lineupIsUpcoming?: boolean,
): FairnessFinding[] {
  const findings: FairnessFinding[] = [];
  const keys = sortedInningKeys(inningLogs);
  const inningsPlayed = keys.length;
  if (inningsPlayed === 0) return findings;

  // Only players who appear in the game (in the lineup or any log) are considered.
  const rosterIds = new Set<string>();
  lineup.forEach(p => rosterIds.add(p.id));
  keys.forEach(k => inningLogs[k].forEach(e => rosterIds.add(e.id)));
  (removedPlayerIds || []).forEach(id => rosterIds.delete(id));

  // Where each player stands in the inning about to start. Rules that describe a
  // situation the coach can still fix (sitting again, short of field innings,
  // stuck at one position) must judge that inning, not the history alone.
  const upcoming = new Map<string, string>();
  if (lineupIsUpcoming) lineup.forEach(p => upcoming.set(p.id, p.position));
  const seatedNext = (id: string): boolean =>
    lineupIsUpcoming ? BENCHED(upcoming.get(id)) || !upcoming.has(id) : true;
  // Field innings including the upcoming one, so "behind pace" reflects the plan.
  const projectedField = (id: string): number =>
    fieldInnings(id) + (lineupIsUpcoming && !seatedNext(id) ? 1 : 0);

  // Field innings (non-bench) per player across the game so far.
  const fieldInnings = (id: string): number => {
    let n = 0;
    keys.forEach(k => {
      const e = inningLogs[k].find(p => p.id === id);
      if (e && !BENCHED(e.position)) n++;
    });
    return n;
  };

  // Bench innings per player across the game so far.
  const benchInnings = (id: string): number => {
    let n = 0;
    keys.forEach(k => {
      const e = inningLogs[k].find(p => p.id === id);
      if (e && BENCHED(e.position)) n++;
    });
    return n;
  };

  const infieldInnings = (id: string): number => {
    let n = 0;
    keys.forEach(k => {
      const e = inningLogs[k].find(p => p.id === id);
      if (e && INFIELD.has(e.position)) n++;
    });
    return n;
  };

  // ── No Repeat Bench: benched the last 2+ consecutive innings ────────────────
  if (settings?.noConsecutiveBench && keys.length >= 2) {
    const last = keys[keys.length - 1];
    const prev = keys[keys.length - 2];
    const benchedLast = (id: string) => {
      const e = inningLogs[last].find(p => p.id === id);
      return e ? BENCHED(e.position) : false;
    };
    const benchedPrev = (id: string) => {
      const e = inningLogs[prev].find(p => p.id === id);
      return e ? BENCHED(e.position) : false;
    };
    // Count the trailing run of consecutive benched innings for a player.
    const streak = (id: string): number => {
      let n = 0;
      for (let i = keys.length - 1; i >= 0; i--) {
        const e = inningLogs[keys[i]].find(p => p.id === id);
        if (e && BENCHED(e.position)) n++;
        else break;
      }
      return n;
    };
    const offenders = [...rosterIds].filter(id => benchedLast(id) && benchedPrev(id) && seatedNext(id));
    if (offenders.length > 0) {
      // Group by streak length for accurate copy.
      const n = streak(offenders[0]);
      const sameStreak = offenders.filter(id => streak(id) === n);
      findings.push({
        rule: 'noConsecutiveBench',
        players: sameStreak,
        message: `${foldNames(sameStreak, rosterNames)} ${haveHas(sameStreak.length)} sat the last ${n} innings.`,
        priority: PRIORITY.noConsecutiveBench,
      });
    }
  }

  // ── Equal Bench Innings: bench spread > 1 across the roster ─────────────────
  if (settings?.equalBenchInnings) {
    const benchCounts = [...rosterIds].map(id => ({
      id,
      b: benchInnings(id) + (lineupIsUpcoming && seatedNext(id) ? 1 : 0),
    }));
    const max = Math.max(...benchCounts.map(c => c.b));
    const min = Math.min(...benchCounts.map(c => c.b));
    if (max - min > 1) {
      const mostBenched = benchCounts.filter(c => c.b === max).map(c => c.id);
      findings.push({
        rule: 'equalBenchInnings',
        players: mostBenched,
        message: `Bench is uneven: ${foldNames(mostBenched, rosterNames)} ${haveHas(mostBenched.length)} sat ${max}, others as few as ${min}.`,
        priority: PRIORITY.equalBenchInnings,
      });
    }
  }

  // ── Min Fielding Innings: flag EARLY (behind pace), not just when impossible ─
  if (settings?.minFieldInningsEnabled && (settings?.minFieldInningsPerPlayer ?? 0) > 0) {
    const required = settings.minFieldInningsPerPlayer;
    const total = totalInningsPlanned && totalInningsPlanned > 0 ? totalInningsPlanned : undefined;
    const remaining = total ? Math.max(0, total - inningsPlayed) : undefined;

    const behind = [...rosterIds].filter(id => {
      const have = projectedField(id);
      if (have >= required) return false;
      if (remaining === undefined) {
        // No game length known: only flag if already short (no projection).
        return have < required;
      }
      // Behind pace (early warning): they need as many remaining innings as are
      // left — there's no slack anymore. Fires the moment the cushion is gone,
      // well before it's mathematically impossible, so the coach has lead time.
      const need = required - have;
      return need >= remaining;
    });

    if (behind.length > 0) {
      findings.push({
        rule: 'minFieldInningsEnabled',
        players: behind,
        message: `${foldNames(behind, rosterNames)} ${areIs(behind.length)} behind pace for the ${required}-inning field minimum.`,
        priority: PRIORITY.minFieldInnings,
      });
    }
  }

  // ── No Repeat Positions: same non-bench position two innings running ────────
  if (settings?.noConsecutivePosition && keys.length >= 2) {
    const last = keys[keys.length - 1];
    const prev = keys[keys.length - 2];
    const repeats: { id: string; pos: string }[] = [];
    if (lineupIsUpcoming) {
      // Compare what the coach just set against the inning that actually ended.
      lineup.forEach(e => {
        if (BENCHED(e.position)) return;
        const before = inningLogs[last].find(p => p.id === e.id);
        if (before && before.position === e.position) repeats.push({ id: e.id, pos: e.position });
      });
    } else {
      inningLogs[last].forEach(e => {
        if (BENCHED(e.position)) return;
        const before = inningLogs[prev].find(p => p.id === e.id);
        if (before && before.position === e.position) repeats.push({ id: e.id, pos: e.position });
      });
    }
    // Collapse to ONE summary line — a per-player list here is just noise, since
    // stable defense means most fielders "repeat." If only one or two repeat,
    // name them; otherwise summarize the count.
    if (repeats.length > 0) {
      const message = repeats.length <= 2
        ? `${foldNames(repeats.map(r => r.id), rosterNames)} ${haveHas(repeats.length)} held the same position as last inning.`
        : `${repeats.length} players are holding the same position as last inning.`;
      findings.push({
        rule: 'noConsecutivePosition',
        players: repeats.map(r => r.id),
        message,
        priority: PRIORITY.noConsecutivePosition,
      });
    }
  }

  // ── Must Play Infield: short of required infield innings ────────────────────
  if (settings?.mustPlayInfieldEnabled && (settings?.mustPlayInfieldInnings ?? 0) > 0) {
    const required = settings.mustPlayInfieldInnings;
    const projectedInfield = (id: string): number =>
      infieldInnings(id) +
      (lineupIsUpcoming && INFIELD.has(upcoming.get(id) ?? '') ? 1 : 0);
    const short = [...rosterIds].filter(id => projectedInfield(id) < required);
    if (short.length > 0) {
      const allZero = short.every(id => projectedInfield(id) === 0);
      const phrase = allZero
        ? `${haveHas(short.length)}n't played an infield inning yet`
        : `${haveHas(short.length)} not reached ${required} infield ${required === 1 ? 'inning' : 'innings'} yet`;
      findings.push({
        rule: 'mustPlayInfieldEnabled',
        players: short,
        message: `${foldNames(short, rosterNames)} ${phrase}.`,
        priority: PRIORITY.mustPlayInfield,
      });
    }
  }

  // Rank by importance so the live box shows what a coach actually cares about
  // first. Capping happens in the UI (top N + "+more").
  return findings.sort((a, b) => a.priority - b.priority);
}

// How many findings the live box shows before collapsing the rest into "+N more".
export const FAIRNESS_VISIBLE_CAP = 3;
